import { useId, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';
import { CHART_MAP, EXTRA_TYPES } from './constants';
import { isCompactDescription, parsePlanDescription } from './outlineUtils';
import styles from './PremiumOutlineTree.module.scss';

// 精简视图默认露出的字段数；其余字段在卡片内展开
const PREVIEW_FIELD_COUNT = 2;

const text = (value) => (typeof value === 'string' && value.trim() ? value : '');

/** 与普通大纲 SectionExtraTags 的取值规则一致 */
function resolveExtraPlans(type, extraDesc, sectionData) {
    if (type === 'image') {
        const charts = Object.entries(sectionData?.chartTypeCn || {}).filter(
            ([key, value]) => CHART_MAP[key] || text(value),
        );
        const fallbackDescription =
            [extraDesc?.image, sectionData?.extraTypeCn?.DIAGRAM].find(text) || '';
        const hasDescription = charts.some(([, value]) => text(value));
        if (charts.length > 0 && (hasDescription || !fallbackDescription)) {
            return charts.map(([key, value]) => ({
                id: `image-${key}`,
                description: text(value),
                fallbackName: CHART_MAP[key]?.label || '',
            }));
        }
        return [{ id: 'image', description: fallbackDescription, fallbackName: '' }];
    }
    if (type === 'webSearch') {
        return [
            {
                id: type,
                description: text(sectionData?.extraTypeCn?.DATA) || text(extraDesc?.webSearch),
                fallbackName: '',
            },
        ];
    }
    return [{ id: type, description: text(extraDesc?.[type]), fallbackName: '' }];
}

// 没有说明文字的标签（表格 / 公式 / 代码）手机上不再重复显示（chipBare），切换按钮本身已点亮
function PlanChip({ config, label }) {
    return (
        <span
            className={classNames(styles.chip, { [styles.chipBare]: !label })}
            style={{ '--plan-color': config.color }}
        >
            <span className={styles.chipIcon}>{config.icon}</span>
            <span className={styles.chipLabel}>{config.label}</span>
            {label && <span className={styles.chipText}>{label}</span>}
        </span>
    );
}

// 长说明按「图名 / 类型 / 字段列表」排版；不可解析时按段落展示并可展开
function PlanCard({ config, description, fallbackName, detail }) {
    const parsed = parsePlanDescription(description, fallbackName);
    const [expanded, setExpanded] = useState(detail === 'full');
    const bodyId = useId();
    const textRef = useRef(null);
    const [clipped, setClipped] = useState(true);

    useLayoutEffect(() => {
        const element = textRef.current;
        if (!element || parsed.structured || expanded) return;
        setClipped(element.scrollHeight > element.clientHeight + 1);
    }, [parsed.structured, parsed.raw, expanded]);

    const hiddenFields = parsed.structured
        ? Math.max(parsed.fields.length - PREVIEW_FIELD_COUNT, 0)
        : 0;
    const canToggle = parsed.structured ? hiddenFields > 0 : expanded || clipped;
    const visibleFields = expanded ? parsed.fields : parsed.fields.slice(0, PREVIEW_FIELD_COUNT);

    return (
        <article
            className={styles.plan}
            style={{ '--plan-color': config.color }}
            aria-label={parsed.title ? `${config.label}方案：${parsed.title}` : `${config.label}方案`}
        >
            <div className={styles.planHeader}>
                <span className={styles.planKind}>
                    {config.icon}
                    {config.label}
                </span>
                {parsed.title ? (
                    <span className={styles.planTitle}>{parsed.title}</span>
                ) : (
                    <span className={styles.planSpacer} aria-hidden />
                )}
                {parsed.type && <span className={styles.planType}>{parsed.type}</span>}
                {canToggle && (
                    <button
                        type="button"
                        className={styles.planToggle}
                        aria-expanded={expanded}
                        aria-controls={bodyId}
                        onClick={(event) => {
                            event.stopPropagation();
                            setExpanded((value) => !value);
                        }}
                    >
                        {expanded
                            ? '收起'
                            : hiddenFields > 0
                              ? `展开其余 ${hiddenFields} 项`
                              : '展开全文'}
                        <ChevronDown
                            size={14}
                            aria-hidden
                            className={classNames(styles.planChevron, {
                                [styles.planChevronOpen]: expanded,
                            })}
                        />
                    </button>
                )}
            </div>
            {parsed.structured ? (
                <dl id={bodyId} className={styles.planFields}>
                    {visibleFields.map((field, index) => (
                        <div key={`${field.key}-${index}`} className={styles.planField}>
                            <dt>{field.key}</dt>
                            <dd>{field.value}</dd>
                        </div>
                    ))}
                </dl>
            ) : (
                <p
                    id={bodyId}
                    ref={textRef}
                    className={classNames(styles.planText, {
                        [styles.planTextClamped]: !expanded,
                    })}
                >
                    {parsed.raw}
                </p>
            )}
        </article>
    );
}

/**
 * 特调大纲里某个章 / 节 / 小节已选插入内容的展示。
 * detail = "compact" 只露出图名、类型和前两项字段；"full" 展开全部字段。
 */
export default function PremiumExtraTags({
    extras = [],
    extraDesc = {},
    sectionData = {},
    detail = 'compact',
}) {
    if (!extras?.length) return null;

    const items = [];
    extras.forEach((type) => {
        const config = EXTRA_TYPES[type];
        if (!config) return;
        resolveExtraPlans(type, extraDesc, sectionData).forEach((plan) =>
            items.push({ ...plan, config }),
        );
    });
    if (!items.length) return null;

    return (
        <div className={styles.plans}>
            {items.map((item) =>
                isCompactDescription(item.description) ? (
                    <PlanChip
                        key={item.id}
                        config={item.config}
                        label={item.description || item.fallbackName}
                    />
                ) : (
                    <PlanCard
                        key={`${item.id}-${detail}`}
                        config={item.config}
                        description={item.description}
                        fallbackName={item.fallbackName}
                        detail={detail}
                    />
                ),
            )}
        </div>
    );
}
